import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Shared URL pre-fill hook for the 3 world-specific builder routes
 * (AA, KP, SB). Mirrors the SB precedent pattern (capture-on-mount via
 * useState initializer + URL clear via setSearchParams replace) but
 * lives in one place so all 3 builders share the contract.
 *
 * Decision #14 + corrected #14B (Sebastian Gate-1 2026-04-27):
 *   - All 3 builders accept `?theme={url-encoded-name}` as the
 *     primary pre-fill source — IdeaTile (Inspiration-Tab) navigates
 *     with this contract.
 *   - SB additionally accepts `?question=` as a back-compat alias
 *     (legacy SB-Question links). Pass `aliasParam: 'question'` to
 *     opt into the alias path.
 *
 * Precedence when both `?theme=` and the alias are present: theme
 * wins (briefing-compliance — `?theme=` is the new canonical
 * contract). The hook returns `null` when neither carries a non-
 * whitespace value, matching the SB precedent's null-fallthrough.
 *
 * The URL is cleared post-mount (replace history, no back-button
 * surfacing of pre-filled state). Subsequent route-level URL changes
 * do NOT re-capture — useState initializer runs once per component
 * instance.
 */

export interface UseBuilderUrlPrefillOptions {
  /** Fallback query-param name (e.g. 'question' on SB for back-compat). */
  aliasParam?: string;
}

export function useBuilderUrlPrefill(options: UseBuilderUrlPrefillOptions = {}): {
  initialValue: string | null;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const [initialValue] = useState(() => {
    // Primary `?theme=` source.
    const theme = searchParams.get('theme');
    if (theme && theme.trim()) return theme;
    // Alias fallback (SB only).
    if (options.aliasParam) {
      const alias = searchParams.get(options.aliasParam);
      if (alias && alias.trim()) return alias;
    }
    return null;
  });

  // Clear pre-fill params on mount via `replace: true` so the
  // back-button doesn't surface the pre-filled URL state.
  useEffect(() => {
    if (initialValue) {
      setSearchParams({}, { replace: true });
    }
  }, [initialValue, setSearchParams]);

  return { initialValue };
}
